import axios, { AxiosInstance, AxiosResponse } from 'axios';

// Base API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
console.log('🔧 API Base URL:', API_BASE_URL);

class ApiService {
  private api: AxiosInstance;
  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000, // 30 seconds timeout (increased from 10s)
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        console.log('🔵 API Request:', {
          method: config.method?.toUpperCase(),
          url: config.url,
          baseURL: config.baseURL,
          fullUrl: `${config.baseURL}${config.url}`
        });
        
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          console.log('🔑 Token added to request');
        } else {
          console.log('⚠️ No token found in localStorage');
        }
        return config;
      },
      (error) => {
        console.error('❌ Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle auth errors
    this.api.interceptors.response.use(
      (response) => {
        console.log('✅ API Response:', {
          status: response.status,
          url: response.config.url,
          data: response.data
        });
        return response;
      },
      (error) => {
        console.error('❌ API Error Details:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          url: error.config?.url,
          method: error.config?.method,
          message: error.response?.data?.message || error.message,
          data: error.response?.data,
          headers: error.response?.headers
        });
        
        // Only logout on 401 for auth-related endpoints or when token is truly invalid
        if (error.response?.status === 401) {
          const url = error.config?.url || '';
          console.log('🚨 401 Unauthorized detected for URL:', url);
          
          // Always logout on auth endpoints
          if (url.includes('/auth/')) {
            console.log('🔐 Auth endpoint 401 - logging out');
            this.handleLogout();
            return Promise.reject(error);
          }
          
          // For other endpoints, check if it's a token validation issue
          const errorMessage = error.response?.data?.message || '';
          console.log('🔍 Checking error message:', errorMessage);
          
          if (errorMessage.toLowerCase().includes('unauthenticated') || 
              errorMessage.toLowerCase().includes('token') ||
              errorMessage.toLowerCase().includes('expired')) {
            console.log('🚨 Token validation issue - logging out');
            this.handleLogout();
          } else {
            console.log('⚠️ 401 but not token issue - keeping session');
          }
        }
        
        // Handle network errors and timeouts without logout
        if (error.code === 'ECONNABORTED') {
          console.log('⏱️ Request timeout - not logging out');
        } else if (error.code === 'ERR_NETWORK') {
          console.log('🌐 Network error - not logging out');
        } else if (error.response?.status === 404) {
          console.log('🔍 404 Not Found - endpoint may not exist');
        } else if (!error.response) {
          console.log('🔌 No response received - possible network issue');
        }
        
        return Promise.reject(error);
      }
    );
  }

  private handleLogout() {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    // Only redirect if not already on login page
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  public get<T = any>(url: string): Promise<AxiosResponse<T>> {
    return this.api.get<T>(url);
  }

  public post<T = any>(url: string, data?: any): Promise<AxiosResponse<T>> {
    return this.api.post<T>(url, data);
  }

  public put<T = any>(url: string, data?: any): Promise<AxiosResponse<T>> {
    return this.api.put<T>(url, data);
  }

  public delete<T = any>(url: string): Promise<AxiosResponse<T>> {
    return this.api.delete<T>(url);
  }

  public setAuthToken(token: string) {
    localStorage.setItem('auth_token', token);
  }

  public removeAuthToken() {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
  }
}

export const apiService = new ApiService();
